import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bug, AlertTriangle, AlertCircle, Info, CheckCircle, Clock, RefreshCw, Wrench, Play, Gamepad2, Map, Ship, TrendingUp, Cpu, Globe, Trophy, Star, Crown, Medal } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { GameSelector } from "@/components/GameSelector";

// Clickable StatCard - navigates to filtered bug list
function StatCard({ title, value, icon: Icon, variant = "default", testId, filterType, filterValue }) {
  const navigate = useNavigate();
  const variants = {
    default: "text-foreground",
    critical: "text-red-500",
    high: "text-orange-500",
    medium: "text-yellow-500",
    low: "text-blue-500",
    primary: "text-primary",
    success: "text-emerald-500",
  };

  const handleClick = () => {
    if (filterType && filterValue && value > 0) {
      navigate(`/bugs?${filterType}=${encodeURIComponent(filterValue)}`);
    }
  };

  const isClickable = filterType && filterValue && value > 0;

  return (
    <Card 
      className={`bg-card border-border/40 transition-all ${
        isClickable 
          ? "hover:border-primary/50 hover:bg-primary/5 cursor-pointer" 
          : ""
      }`}
      onClick={handleClick}
      data-testid={testId}
    >
      <CardContent className="p-3 md:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] md:text-xs text-muted-foreground font-mono uppercase tracking-wider">{title}</p>
            <p className={`text-2xl md:text-3xl font-heading font-bold mt-1 ${variants[variant]}`}>{value}</p>
          </div>
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-sm bg-muted/50 flex items-center justify-center ${variants[variant]}`}>
            <Icon className="w-5 h-5 md:w-6 md:h-6" />
          </div>
        </div>
        {isClickable && (
          <p className="text-[9px] text-muted-foreground font-mono mt-2 opacity-60">Click to view</p>
        )}
      </CardContent>
    </Card>
  );
}

export function SeverityBadge({ severity }) {
  const { t } = useLanguage();
  const styles = {
    Critical: "severity-critical",
    High: "severity-high",
    Medium: "severity-medium",
    Low: "severity-low",
  };

  return (
    <Badge className={`rounded-none px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${styles[severity] || styles.Medium}`}>
      {t(`severities.${severity}`) || severity}
    </Badge>
  );
}

export function StatusBadge({ status }) {
  const { t } = useLanguage();
  const styles = {
    "Needs Verification": "bg-gray-500/10 text-gray-400 border-gray-500/20",
    "Replicated": "bg-purple-500/10 text-purple-400 border-purple-500/20",
    "Verified": "bg-blue-500/10 text-blue-400 border-blue-500/20",
    "In Progress": "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    "Fixed": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    "Unverified": "bg-gray-500/10 text-gray-400 border-gray-500/20",
    "Resolved": "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  const displayText = {
    "Needs Verification": "Needs Verify",
    "In Progress": "In Progress",
  };

  return (
    <Badge className={`rounded-none px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${styles[status] || styles["Needs Verification"]}`}>
      {displayText[status] || t(`statuses.${status}`) || status}
    </Badge>
  );
}

// Heatmap Bar Component
function HeatmapBar({ label, count, maxCount, color = "cyan" }) {
  const navigate = useNavigate();
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
  
  const colorClasses = {
    cyan: "bg-cyan-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
  };

  return (
    <div 
      className="flex items-center justify-between cursor-pointer hover:bg-muted/30 p-1 rounded-sm transition-colors"
      onClick={() => navigate(`/bugs?search=${encodeURIComponent(label)}`)}
    >
      <span className="font-mono text-sm text-muted-foreground truncate max-w-[120px]">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-24 md:w-32 h-2 bg-muted rounded-sm overflow-hidden">
          <div
            className={`h-full ${colorClasses[color]} transition-all`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="font-mono text-xs text-foreground w-8 text-right">{count}</span>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [mapHeatmap, setMapHeatmap] = useState([]);
  const [shipHeatmap, setShipHeatmap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [selectedGame, setSelectedGame] = useState("STAR_ATLAS_UE5");
  const [topLeaders, setTopLeaders] = useState([]);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const fetchData = useCallback(async (game) => {
    try {
      setLoading(true);
      const gameParam = game ? `?game=${game}` : '';
      const [statsRes, mapRes, shipRes, leaderRes] = await Promise.all([
        axios.get(`${API}/stats${gameParam}`),
        axios.get(`${API}/analytics/heatmap/maps${gameParam}`),
        axios.get(`${API}/analytics/heatmap/ships${gameParam}`),
        axios.get(`${API}/leaderboard`, { params: { period: "weekly", limit: 5 } }).catch(() => ({ data: { leaders: [] } }))
      ]);
      setStats(statsRes.data);
      setMapHeatmap(mapRes.data);
      setShipHeatmap(shipRes.data);
      setTopLeaders(leaderRes.data.leaders || []);
      setFetchError(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedGame);
  }, [selectedGame, fetchData]);

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (fetchError || !stats) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-screen">
        <div className="text-center" data-testid="dashboard-error">
          <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
          <h2 className="font-heading text-lg font-bold uppercase tracking-wider text-foreground mb-2">
            Could not load data
          </h2>
          <p className="text-muted-foreground font-body text-sm mb-6">
            The server may be busy or temporarily unavailable. Please try again.
          </p>
          <Button variant="outline" onClick={() => fetchData(selectedGame)} className="font-heading text-xs" data-testid="dashboard-retry-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const maxMapCount = mapHeatmap.length > 0 ? Math.max(...mapHeatmap.map(m => m.count)) : 0;
  const maxShipCount = shipHeatmap.length > 0 ? Math.max(...shipHeatmap.map(s => s.count)) : 0;

  return (
    <div className="p-4 md:p-8 animate-fade-in" data-testid="dashboard-page">
      <div className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-widest text-foreground">{t('dashboard.title')}</h1>
        <div className="cyber-line mt-2" />
        <p className="text-muted-foreground mt-2 font-body text-sm md:text-base">{t('dashboard.subtitle')}</p>
      </div>

      {/* Game Selector */}
      <div className="mb-6">
        <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-3 block">
          Select Game
        </label>
        <GameSelector 
          selectedGame={selectedGame} 
          onGameChange={setSelectedGame}
          size="large"
        />
      </div>

      {/* Severity Stats - Clickable */}
      <div className="mb-6">
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          By Severity
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard 
            title={t('dashboard.totalBugs')} 
            value={stats?.total_bugs || 0} 
            icon={Bug} 
            variant="primary" 
            testId="stat-total"
          />
          <StatCard 
            title={t('dashboard.critical')} 
            value={stats?.critical_count || 0} 
            icon={AlertTriangle} 
            variant="critical" 
            testId="stat-critical"
            filterType="severity"
            filterValue="Critical"
          />
          <StatCard 
            title={t('dashboard.high')} 
            value={stats?.high_count || 0} 
            icon={AlertCircle} 
            variant="high" 
            testId="stat-high"
            filterType="severity"
            filterValue="High"
          />
          <StatCard 
            title={t('dashboard.medium')} 
            value={stats?.medium_count || 0} 
            icon={Info} 
            variant="medium" 
            testId="stat-medium"
            filterType="severity"
            filterValue="Medium"
          />
          <StatCard 
            title={t('dashboard.lowPriority')} 
            value={stats?.low_count || 0} 
            icon={CheckCircle} 
            variant="low" 
            testId="stat-low"
            filterType="severity"
            filterValue="Low"
          />
        </div>
      </div>

      {/* Status Stats - Clickable */}
      <div className="mb-8">
        <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          By Status
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard 
            title="Needs Verify" 
            value={stats?.needs_verification_count || 0} 
            icon={Clock} 
            variant="default" 
            testId="stat-needs-verification"
            filterType="status"
            filterValue="Needs Verification"
          />
          <StatCard 
            title="Replicated" 
            value={stats?.replicated_count || 0} 
            icon={RefreshCw} 
            variant="primary" 
            testId="stat-replicated"
            filterType="status"
            filterValue="Replicated"
          />
          <StatCard 
            title="Verified" 
            value={stats?.verified_count || 0} 
            icon={CheckCircle} 
            variant="primary" 
            testId="stat-verified"
            filterType="status"
            filterValue="Verified"
          />
          <StatCard 
            title="In Progress" 
            value={stats?.in_progress_count || 0} 
            icon={Play} 
            variant="medium" 
            testId="stat-in-progress"
            filterType="status"
            filterValue="In Progress"
          />
          <StatCard 
            title="Fixed" 
            value={stats?.fixed_count || 0} 
            icon={Wrench} 
            variant="success" 
            testId="stat-fixed"
            filterType="status"
            filterValue="Fixed"
          />
        </div>
      </div>

      {/* Heatmaps Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bug Heatmap by Map */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Map className="w-5 h-5 text-cyan-400" />
              Bugs by Map
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {mapHeatmap.length > 0 ? (
              <div className="space-y-2">
                {mapHeatmap.slice(0, 8).map((item) => (
                  <HeatmapBar 
                    key={item.map} 
                    label={item.map} 
                    count={item.count} 
                    maxCount={maxMapCount}
                    color="cyan"
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm font-mono">{t('dashboard.noDataAvailable')}</p>
            )}
          </CardContent>
        </Card>

        {/* Bug Heatmap by Ship */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Ship className="w-5 h-5 text-purple-400" />
              Bugs by Ship
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {shipHeatmap.length > 0 ? (
              <div className="space-y-2">
                {shipHeatmap.slice(0, 8).map((item) => (
                  <HeatmapBar 
                    key={item.ship} 
                    label={item.ship} 
                    count={item.count} 
                    maxCount={maxShipCount}
                    color="purple"
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm font-mono">No ship-specific bugs reported</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category and Classification Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Category Breakdown - Clickable */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Gamepad2 className="w-5 h-5" />
              By Category
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {stats?.category_breakdown && Object.keys(stats.category_breakdown).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(stats.category_breakdown).map(([cat, count]) => (
                  <div 
                    key={cat} 
                    className="flex items-center justify-between cursor-pointer hover:bg-muted/30 p-1 rounded-sm transition-colors"
                    onClick={() => navigate(`/bugs?category=${encodeURIComponent(cat)}`)}
                  >
                    <span className="font-mono text-sm text-muted-foreground">{cat}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 md:w-32 h-2 bg-muted rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 transition-all"
                          style={{ width: `${(count / stats.total_bugs) * 100}%` }}
                        />
                      </div>
                      <span className="font-mono text-xs text-foreground w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm font-mono">{t('dashboard.noDataAvailable')}</p>
            )}
          </CardContent>
        </Card>

        {/* Classification Breakdown - Clickable */}
        <Card className="bg-card border-border/40">
          <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
            <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              {t('dashboard.classificationBreakdown')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {stats?.classification_breakdown && Object.keys(stats.classification_breakdown).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(stats.classification_breakdown).map(([cls, count]) => (
                  <div 
                    key={cls} 
                    className="flex items-center justify-between cursor-pointer hover:bg-muted/30 p-1 rounded-sm transition-colors"
                    onClick={() => navigate(`/bugs?classification=${encodeURIComponent(cls)}`)}
                  >
                    <span className="font-mono text-sm text-muted-foreground">{cls}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 md:w-32 h-2 bg-muted rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all"
                          style={{ width: `${(count / stats.total_bugs) * 100}%` }}
                        />
                      </div>
                      <span className="font-mono text-xs text-foreground w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm font-mono">{t('dashboard.noDataAvailable')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Bug Reports */}
      <Card className="bg-card border-border/40">
        <CardHeader className="border-b border-border/40 bg-muted/20 p-4">
          <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-muted-foreground">
            {t('dashboard.recentBugReports')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {stats?.recent_bugs && stats.recent_bugs.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_bugs.map((bug) => (
                <Link
                  key={bug.id}
                  to={`/bugs/${bug.id}`}
                  className="block p-3 bg-muted/30 rounded-sm hover:bg-muted/50 hover:border-primary/30 border border-transparent transition-colors"
                  data-testid={`recent-bug-${bug.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm border border-primary/20">
                          {bug.display_code || `#${bug.id.slice(0, 8)}`}
                        </span>
                        {bug.category && (
                          <span className="font-mono text-[10px] text-muted-foreground flex items-center gap-1">
                            <Gamepad2 className="w-3 h-3" />
                            {bug.category}
                          </span>
                        )}
                        {bug.impact_score > 0 && (
                          <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-sm ${
                            bug.impact_score >= 70 ? 'bg-red-500/20 text-red-400' :
                            bug.impact_score >= 50 ? 'bg-orange-500/20 text-orange-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            Impact: {bug.impact_score}
                          </span>
                        )}
                      </div>
                      <p className="font-body text-sm text-foreground truncate">{bug.title}</p>
                      <p className="text-xs text-muted-foreground font-mono mt-1">
                        {bug.classification} • {bug.source || 'Manual'}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <SeverityBadge severity={bug.severity} />
                      <StatusBadge status={bug.status} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bug className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm font-mono">{t('dashboard.noBugsReported')}</p>
              <Link
                to="/submit"
                className="inline-block mt-4 px-4 py-2 bg-primary/20 text-primary font-heading font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-primary/30 transition-colors"
                data-testid="submit-first-bug"
              >
                {t('dashboard.submitFirstBug')}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard Widget */}
      <Card className="bg-card border-border/40" data-testid="dashboard-leaderboard-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" />
              Top Contributors (Weekly)
            </CardTitle>
            <Link
              to="/leaderboard"
              className="text-xs text-primary hover:text-primary/80 font-mono transition-colors"
              data-testid="view-full-leaderboard-link"
            >
              View Full Leaderboard
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {topLeaders.length === 0 ? (
            <p className="text-xs text-muted-foreground font-mono text-center py-4">
              No scores yet this week. Report bugs to earn points!
            </p>
          ) : (
            <div className="space-y-2">
              {topLeaders.map((leader, idx) => (
                <div
                  key={leader.user_id}
                  className="flex items-center gap-3 py-1.5"
                  data-testid={`dashboard-leader-${idx}`}
                >
                  <div className="w-6 flex justify-center">
                    {idx === 0 ? <Crown className="w-4 h-4 text-amber-400" /> :
                     idx === 1 ? <Medal className="w-4 h-4 text-slate-300" /> :
                     idx === 2 ? <Medal className="w-4 h-4 text-amber-700" /> :
                     <span className="text-xs font-mono text-muted-foreground">#{idx + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{leader.name || leader.email || "Anonymous"}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className={`w-2.5 h-2.5 ${i <= (leader.reputation_level || 0) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"}`} />
                    ))}
                  </div>
                  <span className="text-sm font-bold font-mono min-w-[3rem] text-right">{leader.weekly_score || 0}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default Dashboard;
